import type { CSSProperties } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Icon,
  V2Card,
  V2CardHeader,
  V2CardBody,
  V2Badge,
  V2Table,
  V2EmptyState,
  type V2BadgeTone,
  type V2TableColumn,
  LoadingState,
  StatePanel,
} from '@sakhtar/design-system/react';
import { HubKpi, HubKpiGrid } from '../../components/hub/HubKpi.js';
import { formatChartAmount } from '../../lib/charts/index.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { PartyLink } from './PartyLink.js';
import { useTenantSettings } from '../../lib/tenantSettings.js';
import { amountColumnHeader, amountUnitLabel, formatRialFa } from '../../lib/finance-display.js';
import { displayJalaliDateTime } from '../../lib/date/displayJalali.js';
import type {
  FulfillmentStatus,
  OverdueActivityItem,
  OverdueActivityReportResponse,
  PipelineReportResponse,
  RecentActivitiesResponse,
  SalesOrder,
  SalesOrderListResponse,
  SalesActionsResponse,
  SalesRepresentativePerformanceResponse,
  SalesRepresentativePerformanceRow,
} from '../../lib/types/crm.js';
import './hub.css';
import { HubYarCard } from '../../shell/yar/HubYarCard.js';

const FULFILLMENT_LABEL: Record<FulfillmentStatus, string> = {
  unreserved: 'رزرو نشده',
  partially_reserved: 'رزرو جزئی',
  ready_to_deliver: 'آماده تحویل',
  partially_delivered: 'تحویل جزئی',
  fully_delivered: 'تحویل کامل',
};

const ORDER_STATUS_TONE: Record<SalesOrder['status'], V2BadgeTone> = {
  draft: 'neutral',
  confirmed: 'success',
  partially_fulfilled: 'info',
  fulfilled: 'success',
  cancelled: 'danger',
};

const fa = (n: number): string => n.toLocaleString('fa-IR');

/** Integer-Rial strings → 0..scale integers relative to the largest (BigInt, no Number on Rial). */
function relativeTo(amounts: readonly string[], scale: number): number[] {
  const values = amounts.map((a) => BigInt(a));
  const max = values.reduce((m, v) => (v > m ? v : m), 0n);
  if (max === 0n) return values.map(() => 0);
  return values.map((v) => Number((v * BigInt(scale)) / max));
}

function FunnelStages(props: {
  readonly stages: PipelineReportResponse['stages'];
  readonly unit: Parameters<typeof formatRialFa>[1];
}) {
  const heights = relativeTo(
    props.stages.map((s) => s.amountRial),
    100,
  );
  const last = props.stages.length - 1;
  return (
    <div className="crm-funnel">
      <div
        className="crm-funnel__grid"
        style={{ '--crm-stages': props.stages.length } as CSSProperties}
      >
        {props.stages.map((s, i) => (
          <div key={s.ref} className="crm-funnel__stage">
            {i > 0 ? (
              <span className="crm-funnel__link" aria-hidden="true">
                <Icon name="chevronLeft" size="sm" />
              </span>
            ) : null}
            <div className="crm-funnel__head">
              <span className="crm-funnel__name">{s.name}</span>
              <span className="crm-funnel__idx sk-num">{fa(i + 1).padStart(2, '۰')}</span>
            </div>
            <div className="crm-funnel__plot">
              <div
                className={`crm-funnel__bar${i === last && last > 0 ? ' crm-funnel__bar--last' : ''}`}
                style={
                  {
                    height: `${Math.max(heights[i] ?? 0, 6)}%`,
                    '--crm-step': last > 0 ? i / last : 0,
                  } as CSSProperties
                }
              />
            </div>
            <div className="crm-funnel__foot">
              <span className="crm-funnel__count">
                {fa(s.leadCount)} سرنخ · {fa(s.probabilityPct)}٪
              </span>
              <span className="crm-funnel__amount sk-num">
                {formatRialFa(s.amountRial, props.unit)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface OverdueRow extends OverdueActivityItem {
  readonly id: string;
}

interface SalesRepresentativeRow extends SalesRepresentativePerformanceRow {
  readonly id: string;
}

/**
 * UI_REFERENCE_SOURCE (primary, 2026-10-11): Sakhtar Cloud ERP v2.dc.html — frame `/t/kimia/sales`
 *   (section `isCrm`; module header + tabs are ModuleLayout's, not repeated here)
 *   v2 status: aligned — hero + tinted KPI tiles (HubKpi), numbered stage funnel with
 *   connectors and total row, recent sales orders table, plus overdue / recent activities
 *   and gated sales actions. Real data only. Documented deviations:
 *   - KPI «لیدهای جدید این هفته» / «فعالیت‌های امروز» are NOT built: no endpoint returns
 *     a lead count for a date window (`GET crm/leads` is a paged list) and
 *     `GET crm/recent-activities` is a 20-row sample, so "today" / "without follow-up"
 *     cannot be computed exactly. The tiles shown instead are exact: open leads
 *     (reports/pipeline), overdue follow-ups (reports/overdue-activities `count`) and
 *     recent activities (recent-activities).
 *   - «N مورد در مذاکره» foot is shown as the busiest stage (stage names are tenant data,
 *     so a hard-coded «مذاکره» stage would be fabricated).
 *   - Sales-orders table omits the reference's «مشتری» name and «تاریخ» columns:
 *     `GET sales/orders` (sales-workflow.controller.ts listOrders) returns only the party
 *     code (`partyRef`) and no created/order date. «تحویل» is the real fulfillment status.
 *   - The funnel header button «گزارش کامل» is «همهٔ سرنخ‌ها» (no funnel report route exists).
 *   - The header CTA buttons / order status filter in the reference have no hub endpoint.
 *
 * Legacy citation follows.
 */
/**
 * UI_REFERENCE_SOURCE: FE-P0 FE-CRM-1 (Chart Intelligence + Report PR #151).
 * Charts CRM-01 (pipeline amount), CRM-02 (overdue activities); CRM-03 (the
 * old per-stage opportunity-count donut) demoted to a compact KPI strip —
 * the amount bar is the decision chart now, per the ticket.
 *
 * Both `GET .../crm/reports/pipeline` and `GET .../crm/reports/overdue-
 * activities` are server-authoritative aggregates (BigInt-summed Rial,
 * business-date "now" via `systemClock` on the server) — this page never
 * sums opportunity/activity rows itself. `GET .../crm/pipeline` (the old
 * count-only endpoint `LeadsPage.tsx` still uses for its own stage
 * picker) is no longer fetched here; the reports endpoint already carries
 * both the count and the amount per stage.
 * state reference: ready · empty · permission_denied · network_error
 * (a 403 hides that section — it never renders a fake zero)
 */
export function CrmHubPage() {
  const { slug } = useParams<{ slug: string }>();
  const { amountDisplayUnit } = useTenantSettings();
  const base = slug ? tenantBasePath(slug) : '';

  const pipeline = useApiGet<PipelineReportResponse>(
    slug ? `${base}/api/v1/crm/reports/pipeline` : null,
    [slug],
  );
  const representativePerformance = useApiGet<SalesRepresentativePerformanceResponse>(
    slug ? `${base}/api/v1/crm/reports/sales-representatives?offset=0&limit=5` : null,
    [slug],
  );
  const overdue = useApiGet<OverdueActivityReportResponse>(
    slug ? `${base}/api/v1/crm/reports/overdue-activities` : null,
    [slug],
  );
  const activities = useApiGet<RecentActivitiesResponse>(
    slug ? `${base}/api/v1/crm/recent-activities` : null,
    [slug],
  );
  const orders = useApiGet<SalesOrderListResponse>(slug ? `${base}/api/v1/sales/orders` : null, [
    slug,
  ]);
  const actions = useApiGet<SalesActionsResponse>(
    slug ? `${base}/api/v1/crm/sales-actions` : null,
    [slug],
  );

  if (!slug) return null;

  const overdueColumns: readonly V2TableColumn<OverdueRow>[] = [
    {
      key: 'title',
      header: 'عنوان',
      render: (row) => <Link to={`${base}/crm/activities`}>{row.title}</Link>,
    },
    {
      key: 'type',
      header: 'نوع',
      render: (row) => <V2Badge tone="neutral">{row.typeLabel}</V2Badge>,
    },
    {
      key: 'due',
      header: 'سررسید',
      render: (row) => (
        <span className="sk-num" style={{ color: 'var(--sk-danger)' }}>
          {displayJalaliDateTime(row.dueAt)}
        </span>
      ),
    },
  ];

  const representativeColumns: readonly V2TableColumn<SalesRepresentativeRow>[] = [
    {
      key: 'rank',
      header: 'رتبه',
      align: 'start',
      render: (row) => <span className="sk-num">{fa(row.rank)}</span>,
    },
    {
      key: 'name',
      header: 'مسئول فروش',
      render: (row) => (
        <span>
          {row.representativeName}
          {row.isCurrentUser ? ' (شما)' : ''}
        </span>
      ),
    },
    {
      key: 'orders',
      header: 'سفارش معتبر',
      align: 'end',
      render: (row) => <span className="sk-num">{fa(row.orderCount)}</span>,
    },
    {
      key: 'total',
      header: amountColumnHeader('فروش', amountDisplayUnit),
      align: 'end',
      render: (row) => (
        <span className="sk-num" style={{ fontWeight: 600 }}>
          {formatRialFa(row.totalRial, amountDisplayUnit)}
        </span>
      ),
    },
  ];

  const orderColumns: readonly V2TableColumn<SalesOrder & { readonly id: string }>[] = [
    {
      key: 'ref',
      header: 'شماره',
      render: (row) => (
        <Link
          to={`${base}/crm/orders/${row.ref}`}
          className="ltr sk-num"
          style={{ fontWeight: 600 }}
        >
          {row.ref}
        </Link>
      ),
    },
    {
      key: 'party',
      header: 'طرف‌حساب',
      render: (row) =>
        row.partyRef ? (
          <span className="ltr">
            <PartyLink base={base} partyRef={row.partyRef} inRow>
              {row.partyRef}
            </PartyLink>
          </span>
        ) : (
          '—'
        ),
    },
    {
      key: 'total',
      header: amountColumnHeader('مبلغ', amountDisplayUnit),
      align: 'end',
      render: (row) => (
        <span className="sk-num" style={{ fontWeight: 600 }}>
          {formatRialFa(row.totalRial, amountDisplayUnit)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (row) => <V2Badge tone={ORDER_STATUS_TONE[row.status]}>{row.statusLabel}</V2Badge>,
    },
    {
      key: 'delivery',
      header: 'تحویل',
      render: (row) =>
        row.fulfillment ? (
          <span style={{ color: 'var(--sk-fg-muted)' }}>
            {FULFILLMENT_LABEL[row.fulfillment.fulfillmentStatus]}
          </span>
        ) : (
          <span style={{ color: 'var(--sk-fg-subtle)' }}>—</span>
        ),
    },
  ];

  // Busiest open stage: an exact derivation from the pipeline report (counts are integers).
  const pipelineData = pipeline.status === 'ready' ? pipeline.data : null;
  const weightedRial =
    pipeline.status === 'ready' ? pipeline.data.totals.weightedAmountRial : undefined;
  const busiest = pipelineData?.stages.reduce<PipelineReportResponse['stages'][number] | null>(
    (best, s) => (best === null || s.leadCount > best.leadCount ? s : best),
    null,
  );

  // Of the recent activities shown, how many are overdue. Only exact when the overdue
  // report was not truncated (it returns at most 100 rows beside its true `count`).
  const overdueRefs =
    overdue.status === 'ready' && overdue.data.count === overdue.data.activities.length
      ? new Set(overdue.data.activities.map((a) => a.ref))
      : null;
  const recentOverdue =
    overdueRefs && activities.status === 'ready'
      ? activities.data.activities.filter((a) => overdueRefs.has(a.ref)).length
      : null;

  const showKpis =
    pipelineData !== null || overdue.status === 'ready' || activities.status === 'ready';

  return (
    <div className="crm-hub">
      <HubYarCard moduleId="crm" />
      {pipeline.status === 'loading' ? <LoadingState /> : null}
      {pipeline.status === 'error' && pipeline.error.status !== 403 ? (
        <StatePanel
          status={pipeline.error.status}
          title="خط فروش قابل نمایش نیست"
          onRetry={pipeline.reload}
        />
      ) : null}

      {showKpis ? (
        <HubKpiGrid accent="crm">
          {pipelineData ? (
            <>
              <HubKpi
                hero
                icon="trend"
                label={`ارزش قیف فروش (${amountUnitLabel(amountDisplayUnit)})`}
                value={formatRialFa(pipelineData.totals.amountRial, amountDisplayUnit)}
                weights={relativeTo(
                  pipelineData.stages.map((s) => s.amountRial),
                  1000,
                )}
                foot="سرنخ‌های در جریان شما"
                footEnd={`${fa(pipelineData.stages.length)} مرحله`}
              />
              <HubKpi
                label="سرنخ‌های در جریان"
                value={fa(pipelineData.totals.leadCount)}
                href={`${base}/crm/leads`}
                {...(busiest && busiest.leadCount > 0
                  ? {
                      foot: `بیشترین در «${busiest.name}»: ${fa(busiest.leadCount)}`,
                      progress: {
                        percent: Math.round(
                          (busiest.leadCount * 100) / pipelineData.totals.leadCount,
                        ),
                      },
                    }
                  : {})}
              />
            </>
          ) : null}
          {overdue.status === 'ready' ? (
            <HubKpi
              label="پیگیری‌های عقب‌افتاده"
              value={fa(overdue.data.count)}
              tone={overdue.data.count > 0 ? 'danger' : undefined}
              href={`${base}/crm/activities`}
              foot={overdue.data.count > 0 ? 'نیازمند پیگیری' : 'همه پیگیری‌ها به‌روز است'}
              footTone={overdue.data.count > 0 ? 'danger' : undefined}
            />
          ) : null}
          {activities.status === 'ready' ? (
            <HubKpi
              label="فعالیت‌های اخیر"
              value={fa(activities.data.activities.length)}
              href={`${base}/crm/activities`}
              {...(recentOverdue !== null && activities.data.activities.length > 0
                ? {
                    foot:
                      recentOverdue > 0
                        ? `${fa(recentOverdue)} مورد عقب‌افتاده`
                        : 'بدون عقب‌افتادگی',
                    footTone: recentOverdue > 0 ? ('danger' as const) : undefined,
                    ticks: { total: activities.data.activities.length, hot: recentOverdue },
                  }
                : {})}
            />
          ) : null}
        </HubKpiGrid>
      ) : null}

      {pipelineData ? (
        <V2Card>
          <V2CardHeader
            icon="filter"
            title="قیف فروش بر اساس مرحله"
            subtitle={
              weightedRial
                ? `${pipelineData.pipelineName} · ارزش وزنی: ${formatChartAmount(weightedRial, { unit: amountDisplayUnit, includeSuffix: true })}`
                : `${pipelineData.pipelineName} · فقط سرنخ‌های در جریان · ${amountUnitLabel(amountDisplayUnit)}`
            }
            actions={
              <Link className="sk-btn sk-btn--secondary sk-btn--sm" to={`${base}/crm/leads`}>
                همهٔ سرنخ‌ها
              </Link>
            }
          />
          {pipelineData.stages.some((s) => s.amountRial !== '0') ? (
            <FunnelStages stages={pipelineData.stages} unit={amountDisplayUnit} />
          ) : (
            <V2CardBody>
              <V2EmptyState icon="sales" compact title="سرنخی در جریان این خط فروش نیست." />
            </V2CardBody>
          )}
          <div className="crm-funnel__total">
            <span>مجموع قیف</span>
            <span className="sk-num">
              {formatRialFa(pipelineData.totals.amountRial, amountDisplayUnit)}
            </span>
          </div>
        </V2Card>
      ) : null}

      {representativePerformance.status === 'loading' ? <LoadingState /> : null}
      {representativePerformance.status === 'error' &&
      representativePerformance.error.status !== 403 ? (
        <StatePanel
          status={representativePerformance.error.status}
          title="عملکرد فروشندگان قابل نمایش نیست"
          onRetry={representativePerformance.reload}
        />
      ) : null}
      {representativePerformance.status === 'ready' ? (
        <V2Card>
          <V2CardHeader
            icon="trend"
            title="عملکرد مسئولان فروش"
            subtitle="سفارش‌های تأییدشده و در حال/کامل تحویل · جمع دقیق ریالی در سرور"
            actions={
              <V2Badge tone="neutral">
                {fa(representativePerformance.data.rows.length)} از{' '}
                {fa(representativePerformance.data.total)}
              </V2Badge>
            }
          />
          <V2Table
            columns={representativeColumns}
            rows={representativePerformance.data.rows.map((row) => ({
              ...row,
              id: String(row.rank),
            }))}
            zebra
            emptyState={
              <V2EmptyState
                icon="sales"
                compact
                title="سفارش معتبر منتسب به مسئول فروش وجود ندارد."
              />
            }
          />
          <div className="crm-funnel__total">
            <span>{fa(representativePerformance.data.totals.orderCount)} سفارش معتبر</span>
            <span className="sk-num">
              {formatRialFa(representativePerformance.data.totals.totalRial, amountDisplayUnit)}
            </span>
          </div>
        </V2Card>
      ) : null}

      {orders.status === 'ready' && orders.data.orders.length > 0 ? (
        <V2Card>
          <V2CardHeader
            icon="invoice"
            title="سفارش‌های فروش اخیر"
            actions={
              <Link className="sk-btn sk-btn--secondary sk-btn--sm" to={`${base}/crm/orders`}>
                همهٔ سفارش‌ها
              </Link>
            }
          />
          <V2Table
            columns={orderColumns}
            rows={orders.data.orders.slice(0, 5).map((o) => ({ ...o, id: o.ref }))}
            zebra
          />
        </V2Card>
      ) : null}

      {overdue.status === 'loading' ? <LoadingState /> : null}
      {overdue.status === 'error' && overdue.error.status !== 403 ? (
        <StatePanel
          status={overdue.error.status}
          title="پیگیری‌های عقب‌افتاده قابل نمایش نیست"
          onRetry={overdue.reload}
        />
      ) : null}
      {overdue.status === 'ready' ? (
        <V2Card>
          <V2CardHeader
            icon="clock"
            title="پیگیری‌های عقب‌افتاده"
            actions={
              <V2Badge tone={overdue.data.count > 0 ? 'danger' : 'neutral'}>
                {fa(overdue.data.count)}
              </V2Badge>
            }
          />
          <V2Table
            columns={overdueColumns}
            rows={overdue.data.activities.map((a) => ({ ...a, id: a.ref }))}
            zebra
            emptyState={<V2EmptyState icon="check" compact title="پیگیری عقب‌افتاده‌ای نیست." />}
          />
        </V2Card>
      ) : null}

      {activities.status === 'error' && activities.error.status === 403 ? null : (
        <V2Card>
          <V2CardHeader
            icon="tasks"
            title="فعالیت‌های اخیر"
            actions={
              <Link className="sk-btn sk-btn--secondary sk-btn--sm" to={`${base}/crm/activities`}>
                همهٔ فعالیت‌ها
              </Link>
            }
          />
          {activities.status === 'loading' ? (
            <V2CardBody>
              <LoadingState />
            </V2CardBody>
          ) : null}
          {activities.status === 'error' && activities.error.status !== 403 ? (
            <V2CardBody>
              <StatePanel
                status={activities.error.status}
                title="فعالیت‌ها قابل نمایش نیست"
                onRetry={activities.reload}
              />
            </V2CardBody>
          ) : null}
          {activities.status === 'ready' ? (
            activities.data.activities.length === 0 ? (
              <V2EmptyState icon="info" compact title="فعالیتی ثبت نشده" />
            ) : (
              <div className="crm-rows">
                {activities.data.activities.map((a) => (
                  <div key={a.ref} className="crm-row">
                    <span className="crm-row__title">{a.title}</span>
                    <div className="crm-row__meta">
                      {a.dueAt ? (
                        <span
                          className="sk-num"
                          style={{ fontSize: 11.5, color: 'var(--sk-fg-muted)' }}
                        >
                          {displayJalaliDateTime(a.dueAt)}
                        </span>
                      ) : null}
                      <V2Badge tone="neutral">{a.activityTypeLabel}</V2Badge>
                      <V2Badge tone="info">{a.statusLabel}</V2Badge>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : null}
        </V2Card>
      )}

      {actions.status === 'ready' && actions.data.actions.length > 0 ? (
        <V2Card>
          <V2CardHeader icon="layers" title="اقدامات فروش" />
          <div className="crm-rows">
            {actions.data.actions.map((a) => {
              const tone: V2BadgeTone = a.availability === 'available' ? 'success' : 'neutral';
              return (
                <div key={a.label} className="crm-row">
                  <span className="crm-row__title">{a.label}</span>
                  <V2Badge tone={tone}>
                    {a.availability === 'available' ? 'در دسترس' : (a.blockedReason ?? 'مسدود')}
                  </V2Badge>
                </div>
              );
            })}
          </div>
        </V2Card>
      ) : null}
    </div>
  );
}
